import React, {useState, useEffect} from 'react';
import {CardContent, Typography, Box} from '@material-ui/core';
import styled from 'styled-components';
import {Skeleton} from '@material-ui/lab';

const Banner = styled.div`
    min-height: 150px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;

    .img_banner {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 5px;
    }
`;

const Logo = styled.div`
    min-height: 150px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;

    .img_logo {
        max-width: 200px;
        max-height: 150px;
        object-fit: contain;
        border-radius: 5px;
    }
`;

const PageHeader = styled.header`
    width: 100%;
`;

const stripHtmlTags = (html) => {
	if (!html) return '';
	const tmp = document.createElement('div');
	tmp.innerHTML = html;
	return tmp.textContent || tmp.innerText || '';
};

const ProductInfo = ({product, loading}) => {
	const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 992);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth <= 992);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	if (loading) {
		return (
			<div className="w-100">
				<Skeleton variant="rectangular" height={300}/>
				<CardContent sx={{textAlign: 'center'}}>
					<Box sx={{width: '50%', height: 36, mx: 'auto'}}>
						<Skeleton variant="text" width="100%" height="100%"/>
					</Box>
					<Box sx={{width: '70%', mx: 'auto'}}>
						<Skeleton variant="text" width="100%"/>
					</Box>
					<Box sx={{width: '85%', mx: 'auto'}}>
						<Skeleton variant="text" width="100%"/>
					</Box>
				</CardContent>
			</div>
		);
	}

	// Return null if no product OR if product has neither banner nor logo
	if (!product || (!product.banner && !product.smallBanner && !product.logo)) return null;

	// Get the appropriate banner based on screen size
	const getBannerSrc = () => {
		if (isSmallScreen) {
			return product.smallBanner || product.banner;
		}
		return product.banner || product.smallBanner;
	};

	// Get page type from URL for structured data
	const pathSegments = window.location.pathname.split("/").filter(Boolean);
	const pageType = pathSegments[0] === "partner" ? "partner" : pathSegments[0] === "page" ? "landing" : "page";

	return (
		<PageHeader className={"w-100"} itemScope itemType="https://schema.org/WebPage">
			{product.mainType === "banner" ?
				<Banner>
					<img
						src={getBannerSrc()}
						className="img_banner"
						alt={`${product.title} - Shop products at Delishop`}
						title={product.title}
						loading="eager"
						fetchpriority="high"
						width="1200"
						height="300"
						itemProp="image"
					/>
				</Banner> :
				<Logo>
					<img
						src={product.logo}
						className="img_logo"
						alt={`${product.title} logo`}
						title={product.title}
						loading="eager"
						fetchpriority="high"
						width="200"
						height="150"
						itemProp="image"
					/>
				</Logo>
			}
			<CardContent>
				<Typography
					variant="h1"
					component="h1"
					style={{fontSize: '2rem', fontWeight: "bold"}}
					align="center"
					gutterBottom
					itemProp="name"
				>
					{product.title}
				</Typography>
				<Typography
					variant="body1"
					component="div"
					align="center"
					color="text.secondary"
					itemProp="description"
				>
					<div
						className="ql-snow"
						dangerouslySetInnerHTML={{
							__html: product?.description,
						}}
					></div>
				</Typography>

				{/* Hidden meta description for SEO (plain text version) */}
				<meta itemProp="description" content={stripHtmlTags(product?.description)}/>
			</CardContent>
		</PageHeader>
	);
};

export default ProductInfo;